import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { serverApi } from '../../../api';

const MySwal = withReactContent(Swal);

export const GestionarModuloClasesModal = ({ show, onClose, onChange }) => {
    const [clases, setClases] = useState([]);
    const [nombreClase, setNombreClase] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingUid, setEditingUid] = useState(null);
    const [editingNombre, setEditingNombre] = useState('');

    const cargarClases = useCallback(async () => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        if (!codigoEntidad) return;

        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/lista-moduloClases', { codigoEntidad });
            if (resp.data.ok) {
                setClases(resp.data.clases || []);
            }
        } catch (error) {
            console.error('Error al cargar clases de módulo:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (show) {
            cargarClases();
            setNombreClase('');
            setEditingUid(null);
        }
    }, [show, cargarClases]);

    const handleCrearClase = async (e) => {
        e.preventDefault();
        if (!nombreClase.trim()) {
            return MySwal.fire('Atención', 'Ingrese el nombre de la clase de módulo', 'warning');
        }

        const codigoEntidad = localStorage.getItem('codigoEntidad');
        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/registra-moduloClase', {
                codigoEntidad,
                moduloClase: nombreClase.trim(),
            });

            if (resp.data.ok) {
                MySwal.fire({
                    title: '¡Clase creada!',
                    text: resp.data.resp,
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                });
                setNombreClase('');
                cargarClases();
                if (onChange) onChange();
            } else {
                MySwal.fire('Atención', resp.data.resp || 'No se pudo crear la clase', 'error');
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al crear la clase de módulo', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGuardarEdicion = async (uidModuloClase) => {
        if (!editingNombre.trim()) return setEditingUid(null);

        const codigoEntidad = localStorage.getItem('codigoEntidad');
        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/edita-moduloClase', {
                codigoEntidad,
                uidModuloClase,
                moduloClase: editingNombre.trim(),
            });

            if (resp.data.ok) {
                MySwal.fire({
                    title: '¡Actualizado!',
                    text: resp.data.resp,
                    icon: 'success',
                    timer: 1200,
                    showConfirmButton: false,
                });
                setEditingUid(null);
                cargarClases();
                if (onChange) onChange();
            } else {
                MySwal.fire('Atención', resp.data.resp, 'error');
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al editar la clase de módulo', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEstado = async (uidModuloClase, estadoActual) => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        const nuevoEstado = estadoActual === 1 ? 0 : 1;
        const accion = nuevoEstado === 1 ? 'activar' : 'inactivar';

        const result = await MySwal.fire({
            title: '¿Está seguro?',
            text: `¿Desea ${accion} esta clase de módulo?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0d7367',
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/inactiva-moduloClase', {
                codigoEntidad,
                uidModuloClase,
                accion: nuevoEstado,
            });

            if (resp.data.ok) {
                cargarClases();
                if (onChange) onChange();
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al cambiar estado de la clase', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold fs-4" style={{ color: '#0d7367' }}>
                    🏷️ Gestión de Clases de Módulo
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {/* Formulario de creación de clase */}
                <Form onSubmit={handleCrearClase} className="mb-4 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Form.Group className="mb-2">
                        <Form.Label className="fw-bold text-secondary">Nueva Clase de Módulo</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                placeholder="Ej: Taquilla, Consultorio, Ventanilla, Admisiones..."
                                value={nombreClase}
                                onChange={(e) => setNombreClase(e.target.value.toUpperCase())}
                                style={{ borderRadius: 10 }}
                            />
                            <Button
                                type="submit"
                                disabled={loading || !nombreClase.trim()}
                                style={{
                                    background: 'linear-gradient(135deg, #0d7367 0%, #14b8a6 100%)',
                                    border: 'none',
                                    borderRadius: 10,
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    padding: '8px 20px',
                                }}
                            >
                                ➕ Crear Clase
                            </Button>
                        </div>
                    </Form.Group>
                </Form>

                {/* Tabla de clases */}
                <div className="table-responsive" style={{ maxHeight: 350, overflowY: 'auto' }}>
                    <Table hover align="middle">
                        <thead>
                            <tr style={{ background: '#f1f5f9', fontSize: 13, textTransform: 'uppercase' }}>
                                <th style={{ width: '10%' }}>Estado</th>
                                <th>Nombre Clase</th>
                                <th style={{ width: '25%', textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clases.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="text-center text-muted py-4">
                                        No hay clases de módulo registradas.
                                    </td>
                                </tr>
                            ) : (
                                clases.map((item) => (
                                    <tr key={item.uidModuloClase}>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant={item.indActivo === 1 ? 'danger' : 'success'}
                                                onClick={() => handleToggleEstado(item.uidModuloClase, item.indActivo)}
                                                title={item.indActivo === 1 ? 'Inactivar' : 'Activar'}
                                            >
                                                <i className={`fa-solid ${item.indActivo === 1 ? 'fa-minus' : 'fa-plus'}`}></i>
                                            </Button>
                                        </td>
                                        <td>
                                            {editingUid === item.uidModuloClase ? (
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={editingNombre}
                                                    onChange={(e) => setEditingNombre(e.target.value.toUpperCase())}
                                                    onBlur={() => handleGuardarEdicion(item.uidModuloClase)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleGuardarEdicion(item.uidModuloClase);
                                                        if (e.key === 'Escape') setEditingUid(null);
                                                    }}
                                                    autoFocus
                                                    style={{ borderRadius: 8 }}
                                                />
                                            ) : (
                                                <span
                                                    style={{
                                                        fontWeight: 700,
                                                        color: item.indActivo === 1 ? '#0f172a' : '#94a3b8',
                                                        textDecoration: item.indActivo === 1 ? 'none' : 'line-through',
                                                    }}
                                                >
                                                    {item.moduloClase.toUpperCase()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => {
                                                    setEditingUid(item.uidModuloClase);
                                                    setEditingNombre(item.moduloClase);
                                                }}
                                                style={{ borderRadius: 8 }}
                                            >
                                                ✏️ Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="secondary" onClick={onClose} style={{ borderRadius: 10, fontWeight: 700 }}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
